/**
 * The authenticated-user control in the top bar: initials avatar, name + role,
 * and ONE menu for everything personal — language, theme, user management,
 * change password, sign out. Under AUTH_MODE=none it still renders (the
 * synthetic operator), but sign-out and change-password are hidden — there
 * is no session to end.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import LockResetIcon from "@mui/icons-material/LockReset";
import LogoutIcon from "@mui/icons-material/Logout";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import PersonIcon from "@mui/icons-material/PersonOutline";
import SettingsBrightnessIcon from "@mui/icons-material/SettingsBrightness";
import { useThemeMode, type ThemePref } from "../hooks/useThemeMode";
import { setLanguage } from "../i18n";
import { useAuth } from "./useAuth";
import { ChangePasswordDialog } from "./ChangePasswordDialog";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function UserMenu() {
  const { t, i18n } = useTranslation();
  const { enabled, mode, user, isSysadmin, logout } = useAuth();
  const { pref: themePref, setPref: setThemePref } = useThemeMode();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [pwdOpen, setPwdOpen] = useState(false);

  if (!user) return null;

  const roleLabel = t(`auth.role.${user.role}`, user.role);
  const languages: { code: "en" | "fr"; label: string }[] = [
    { code: "en", label: t("app.english") },
    { code: "fr", label: t("app.french") },
  ];
  const themes: { pref: ThemePref; label: string; icon: React.ReactElement }[] = [
    { pref: "light", label: t("app.themeLight"), icon: <LightModeIcon fontSize="small" /> },
    { pref: "dark", label: t("app.themeDark"), icon: <DarkModeIcon fontSize="small" /> },
    {
      pref: "system",
      label: t("app.themeSystem"),
      icon: <SettingsBrightnessIcon fontSize="small" />,
    },
  ];
  const check = <CheckIcon fontSize="small" sx={{ ml: "auto", pl: 1 }} />;

  return (
    <>
      <IconButton
        onClick={(e) => setAnchor(e.currentTarget)}
        size="small"
        aria-label={t("auth.accountMenu")}
        aria-haspopup="menu"
      >
        <Avatar sx={{ bgcolor: "primary.main", width: 36, height: 36, fontSize: 14 }}>
          {initials(user.full_name)}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
            {user.full_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            {user.email}
          </Typography>
          <Chip
            label={roleLabel}
            size="small"
            color={isSysadmin ? "primary" : "default"}
            sx={{ mt: 1, fontWeight: 600 }}
          />
        </Box>
        <Divider />

        <ListSubheader sx={{ lineHeight: "32px", bgcolor: "transparent" }}>
          {t("app.language")}
        </ListSubheader>
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            selected={i18n.language.startsWith(lang.code)}
            onClick={() => {
              setLanguage(lang.code);
              setAnchor(null);
            }}
          >
            {lang.label}
            {i18n.language.startsWith(lang.code) && check}
          </MenuItem>
        ))}
        <ListSubheader sx={{ lineHeight: "32px", bgcolor: "transparent" }}>
          {t("app.theme")}
        </ListSubheader>
        {themes.map((choice) => (
          <MenuItem
            key={choice.pref}
            selected={themePref === choice.pref}
            onClick={() => {
              setThemePref(choice.pref);
              setAnchor(null);
            }}
          >
            <ListItemIcon>{choice.icon}</ListItemIcon>
            {choice.label}
            {themePref === choice.pref && check}
          </MenuItem>
        ))}
        <Divider />

        {isSysadmin && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              void navigate("/admin/users");
            }}
          >
            <ListItemIcon>
              <ManageAccountsIcon fontSize="small" />
            </ListItemIcon>
            {t("auth.manageUsers")}
          </MenuItem>
        )}

        {/* only platform accounts have a password to change; under OIDC the
            identity provider owns it and this entry led nowhere */}
        {enabled && mode === "local" && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              setPwdOpen(true);
            }}
          >
            <ListItemIcon>
              <LockResetIcon fontSize="small" />
            </ListItemIcon>
            {t("auth.changePassword")}
          </MenuItem>
        )}

        {enabled ? (
          <MenuItem
            onClick={async () => {
              setAnchor(null);
              await logout();
              void navigate("/login");
            }}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" color="error" />
            </ListItemIcon>
            {t("auth.signOut")}
          </MenuItem>
        ) : (
          <MenuItem disabled>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            {t("auth.noSessionMode")}
          </MenuItem>
        )}
      </Menu>

      {enabled && (
        <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
      )}
    </>
  );
}
